import { ApiResponse } from "@src/types/httpApi";
import { ICuratorShowPromo, IShowRecord } from "@src/types/show";
import { RoleType } from "@src/types/user";
import httpApi from "@src/utils/httpApi";
import { AxiosResponse } from "axios";
import moment from "moment";

export interface IShowDetails {
  documentId: string;
  title: string;
  frequency: string | null;
  curatorName: string | null;
  email: string | null;
  phone: string | null;
}

// Fetches the details shown in the schedule-calendar hover tooltip for a single
// show: its frequency (a scheduling-cadence enum) plus the primary curator's
// name, email and phone.
//
// Uses the dedicated GET /shows/:documentId/contact-details endpoint, which
// joins the data server-side: email lives on the curator's users-permissions
// user (stripped from a plain REST populate) and phone on the client record
// keyed by that user id, so neither is reachable via a direct populate here.
export const fetchShowDetails = async (
  documentId: string
): Promise<IShowDetails> => {
  const response = await httpApi.get(
    `/api/shows/${documentId}/contact-details`
  );
  const details = response.data?.data;

  return {
    documentId: details?.documentId ?? documentId,
    title: details?.title ?? "",
    frequency: details?.frequency ?? null,
    curatorName: details?.curatorName ?? null,
    email: details?.email ?? null,
    phone: details?.phone ?? null,
  };
};

// Counts a show's archived playlists that actually appear in the public
// archive, mirroring the getPlaylistArchivesPublic filter: playlistStatus
// "archived", a releaseDate that has already passed, and a SoundCloud link
// (soundcloudPermalinkUrl). Returns a map keyed by the show's documentId,
// fetched as a single grouped pass (minimal fields, paged) rather than one
// request per show.
export const fetchArchivedPlaylistCountsByShow = async (): Promise<
  Record<string, number>
> => {
  const counts: Record<string, number> = {};
  const pageSize = 1000;
  const now = new Date().toISOString();
  let page = 1;
  let pageCount = 1;

  do {
    const response = await httpApi.get(`/api/playlists`, {
      params: {
        "filters[playlistStatus][$eq]": "archived",
        "filters[releaseDate][$notNull]": "true",
        "filters[releaseDate][$lte]": now,
        "filters[soundcloudPermalinkUrl][$notNull]": "true",
        "fields[0]": "id",
        "populate[show][fields][0]": "documentId",
        "pagination[page]": page,
        "pagination[pageSize]": pageSize,
      },
    });

    const items: Array<{ show?: { documentId?: string } | null }> =
      response.data?.data ?? [];
    items.forEach((item) => {
      const showDocumentId = item?.show?.documentId;
      if (showDocumentId) {
        counts[showDocumentId] = (counts[showDocumentId] ?? 0) + 1;
      }
    });

    pageCount = response.data?.meta?.pagination?.pageCount ?? 1;
    page += 1;
  } while (page <= pageCount);

  return counts;
};

// Counts a show's "submitted" playlists (playlistStatus "submitted"), the same
// status the schedule calendar uses to fill its red slots. Returns a map keyed
// by the show's documentId, fetched as a single grouped pass (minimal fields,
// paged) rather than one request per show.
export const fetchSubmittedPlaylistCountsByShow = async (): Promise<
  Record<string, number>
> => {
  const counts: Record<string, number> = {};
  const pageSize = 1000;
  let page = 1;
  let pageCount = 1;

  do {
    const response = await httpApi.get(`/api/playlists`, {
      params: {
        "filters[playlistStatus][$eq]": "submitted",
        "fields[0]": "id",
        "populate[show][fields][0]": "documentId",
        "pagination[page]": page,
        "pagination[pageSize]": pageSize,
      },
    });

    const items: Array<{ show?: { documentId?: string } | null }> =
      response.data?.data ?? [];
    items.forEach((item) => {
      const showDocumentId = item?.show?.documentId;
      if (showDocumentId) {
        counts[showDocumentId] = (counts[showDocumentId] ?? 0) + 1;
      }
    });

    pageCount = response.data?.meta?.pagination?.pageCount ?? 1;
    page += 1;
  } while (page <= pageCount);

  return counts;
};

export const fetchShows = (params?: {
  curatorId: number;
  roleType: RoleType;
  searchText: String;
}): Promise<AxiosResponse<ApiResponse<IShowRecord[]>>> => {
  const queryParams: any = {
    populate: {
      curators: {
        fields: ["name"],
      },
    },
    pagination: {
      page: 1,
      pageSize: 1000,
    },
  };
  if (
    params?.curatorId &&
    params.roleType !== "admin" &&
    params.roleType !== "manager"
  ) {
    queryParams["filters[curators][id][$eq]"] = params.curatorId;
  }
  if (params?.searchText) {
    queryParams["filters[$or][0][title][$containsi]"] = params.searchText;
    queryParams["filters[$or][1][id][$containsi]"] = params.searchText;
    queryParams["filters[$or][2][documentId][$containsi]"] = params.searchText;
  }
  queryParams["sort"] = ["title:asc"];

  return httpApi.get(`/api/shows`, { params: queryParams });
};

// How far around "now" we look for the airing that prefills a story's day and
// time. Backwards so a show between scheduling runs still has a weekday to
// offer, forwards far enough to cover a monthly cadence.
const PROMO_AIRING_PAST_DAYS = 30;
const PROMO_AIRING_FUTURE_DAYS = 90;

// Every show documentId adds its own `filters[show][documentId][$in][N]` param,
// so batch them the same way the schedule calendar does and keep each URL well
// under the server's URI length limit.
const PROMO_SHOW_BATCH_SIZE = 20;

const PROMO_SCHEDULE_PAGE_SIZE = 500;

type PromoScheduleEvent = {
  startTime: string;
  show?: { documentId?: string } | null;
};

const fetchPromoAiringsByShow = async (
  showDocumentIds: string[]
): Promise<Record<string, { next?: string; previous?: string }>> => {
  const now = moment();
  const airings: Record<string, { next?: string; previous?: string }> = {};

  const batches: string[][] = [];
  for (let i = 0; i < showDocumentIds.length; i += PROMO_SHOW_BATCH_SIZE) {
    batches.push(showDocumentIds.slice(i, i + PROMO_SHOW_BATCH_SIZE));
  }

  const requestPage = (
    ids: string[],
    page: number
  ): Promise<AxiosResponse<ApiResponse<PromoScheduleEvent[]>>> => {
    const params: Record<string, string | number | string[]> = {
      "filters[startTime][$gte]": now
        .clone()
        .subtract(PROMO_AIRING_PAST_DAYS, "days")
        .toISOString(),
      "filters[startTime][$lt]": now
        .clone()
        .add(PROMO_AIRING_FUTURE_DAYS, "days")
        .toISOString(),
      "fields[0]": "startTime",
      "populate[show][fields][0]": "documentId",
      sort: ["startTime:asc"],
      "pagination[page]": page,
      "pagination[pageSize]": PROMO_SCHEDULE_PAGE_SIZE,
    };
    ids.forEach((documentId, index) => {
      params[`filters[show][documentId][$in][${index}]`] = documentId;
    });
    return httpApi.get<ApiResponse<PromoScheduleEvent[]>>("/api/schedules", {
      params,
    });
  };

  // Walk every page. Events come back oldest-first, so stopping at page one
  // would spend the whole page on past airings for a busy batch and leave a
  // show that does have an upcoming one prefilled from an old one, or from the
  // placeholders.
  const fetchBatch = async (ids: string[]): Promise<PromoScheduleEvent[]> => {
    const firstResponse = await requestPage(ids, 1);
    const pageCount = firstResponse.data?.meta?.pagination?.pageCount ?? 1;
    const events = [...(firstResponse.data?.data ?? [])];

    for (let page = 2; page <= pageCount; page += 1) {
      const response = await requestPage(ids, page);
      events.push(...(response.data?.data ?? []));
    }

    return events;
  };

  const batchedEvents = await Promise.all(batches.map(fetchBatch));

  // Oldest-first, so the first future event is the next airing and the last
  // past one is the most recent.
  batchedEvents.flat().forEach((event) => {
    const showDocumentId = event.show?.documentId;
    if (!showDocumentId || !event.startTime) return;

    const entry = airings[showDocumentId] ?? {};
    if (moment(event.startTime).isSameOrAfter(now)) {
      entry.next = entry.next ?? event.startTime;
    } else {
      entry.previous = event.startTime;
    }
    airings[showDocumentId] = entry;
  });

  return airings;
};

// Shows hosted by a curator, each carrying the airing the Instagram Story
// template should be prefilled from. Ordered so the show with the soonest
// upcoming airing comes first — that is the one a promo is almost always for.
export const fetchCuratorShowPromos = async (
  curatorDocumentId: string
): Promise<ICuratorShowPromo[]> => {
  if (!curatorDocumentId) return [];

  const showsResponse = await httpApi.get<ApiResponse<IShowRecord[]>>(
    "/api/shows",
    {
      params: {
        "filters[curators][documentId][$eq]": curatorDocumentId,
        "fields[0]": "title",
        sort: ["title:asc"],
        "pagination[page]": 1,
        "pagination[pageSize]": 100,
      },
    }
  );

  const shows = (showsResponse.data?.data ?? []).filter(
    (show) => show?.documentId
  );
  if (!shows.length) return [];

  const airings = await fetchPromoAiringsByShow(
    shows.map((show) => show.documentId)
  );

  return shows
    .map((show) => {
      const airing = airings[show.documentId] ?? {};
      return {
        documentId: show.documentId,
        title: show.title ?? "",
        airingStartTime: airing.next ?? airing.previous ?? null,
        // Sort key only: an upcoming airing outranks a past one, and among
        // upcoming ones the soonest wins.
        sortRank: airing.next
          ? moment(airing.next).valueOf()
          : Number.MAX_SAFE_INTEGER,
      };
    })
    .sort((a, b) => a.sortRank - b.sortRank)
    .map(({ documentId, title, airingStartTime }) => ({
      documentId,
      title,
      airingStartTime,
    }));
};
